import useGroups from "@/hooks/useGroups";
import useRefDimensions from "@/hooks/useRefDimensions";
import useUsers from "@/hooks/useUsers";
import { User } from "@/includes";
import useGlobalStore from "@/services/store/useGlobalStore";
import { DEFAULT_GROUP_ID } from "@/utils/constants/group";
import { logger } from "@/utils/functions/logger";
import { CloseOutlined } from "@mui/icons-material";
import { Box, Checkbox, FormControlLabel, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { useRef } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import CreateGroup from "./component/CreateGroup";
import GroupName from "./component/GroupName";

const UserList = () => {
  const theme = useTheme();

  const { selectedUsers, setSelectedUsers } = useGlobalStore((state) => state);
  const { usersByGroupId, usersById } = useUsers();

  const { groupsById, addUserToGroup, removeUserFromGroup, deleteGroup } = useGroups();

  const allGroupIds = [DEFAULT_GROUP_ID, ...Object.keys(groupsById)];

  const isSelected = (userId: number) => !!selectedUsers.find((selectedUserId: number) => selectedUserId === userId);

  const handleGroupClick = (groupId: string) => () => {
    try {
      const groupUserIds = usersByGroupId[groupId];
      if (usersByGroupId[groupId]?.every(isSelected)) {
        setSelectedUsers(
          selectedUsers.filter(
            (selectedUserId: number) => !groupUserIds.find((userId: number) => userId === selectedUserId)
          )
        );
      } else {
        setSelectedUsers([...new Set([...selectedUsers, ...groupUserIds.map((userId: number) => userId)])]);
      }
    } catch (e) {
      logger.error(e);
    }
  };

  const ref = useRef(null);
  const { height } = useRefDimensions(ref);

  const getGroupToUpdate = (source: { droppableId: string; index: number }) => {
    return groupsById[source.droppableId];
  };

  const getUserToMove = (source: { droppableId: string; index: number }) => {
    const { droppableId, index } = source;
    return usersById[usersByGroupId[droppableId][index]];
  };

  const canUpdateGroup = (group: GroupType) => {
    if (
      group !== undefined &&
      group.users.filter(
        (user) =>
          (user.mission?.type === "FOLLOW_LEADER" || user.mission?.type === "TEAM_POSITION") &&
          user.mission?.status === "ONGOING"
      ).length > 0
    ) {
      return false;
    }

    return true;
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }
    const sInd = source.droppableId;
    const dInd = destination.droppableId;

    if (sInd !== dInd) {
      const { droppableId } = destination;
      const userToMove = getUserToMove(source);

      // Cancel the drop if a user of the source group is on follow leader or team follow
      const sourceGroupToUpdate = getGroupToUpdate(source);
      const destinationGroupToUpdate = getGroupToUpdate(destination);
      if (!canUpdateGroup(sourceGroupToUpdate) || !canUpdateGroup(destinationGroupToUpdate)) {
        alert("Un membre du groupe à modifier a une mission qui ne permet pas de déplacer un utilisateur.");
        return;
      }

      if (droppableId !== DEFAULT_GROUP_ID) {
        addUserToGroup(userToMove.id, parseInt(droppableId, 10));
      } else {
        removeUserFromGroup(userToMove.id, parseInt(source.droppableId, 10));
      }
    }
  };

  return (
    <Stack
      sx={{
        background: (theme: ThemeType) => theme.palette.secondary.main,
        borderRadius: 2,
        width: "100%",
        height: "100%",
      }}
      alignItems="center">
      <Stack direction="column" sx={{ p: "8px 0", boxSizing: "border-box", height: "100%", width: "95%" }}>
        <Box ref={ref} sx={{ p: "0 0 8px 0" }}>
          <CreateGroup />
        </Box>
        <Box height={`calc(100% - ${height}px)`} sx={{ padding: "0 8px 0 0", overflowY: "auto", overflowX: "hidden" }}>
          <DragDropContext onDragEnd={onDragEnd}>
            {allGroupIds.map((groupId: string) => (
              <Stack key={groupId} width="100%" alignItems="end">
                <Stack width="100%" justifyContent="space-between" direction="row">
                  <FormControlLabel
                    label={
                      groupId === DEFAULT_GROUP_ID ? (
                        <Typography sx={{ color: (theme: ThemeType) => theme.palette.primary.contrastText }}>
                          Équipiers
                        </Typography>
                      ) : (
                        <GroupName groupId={groupId} groupName={groupsById[groupId]?.name} />
                      )
                    }
                    control={
                      <Checkbox
                        sx={{ color: (theme: ThemeType) => theme.palette.primary.contrastText }}
                        onChange={handleGroupClick(groupId)}
                        disabled={usersByGroupId[groupId]?.length === 0}
                        indeterminate={
                          usersByGroupId[groupId]?.filter(isSelected)?.length < usersByGroupId[groupId]?.length &&
                          usersByGroupId[groupId]?.filter(isSelected)?.length > 0
                        }
                        checked={
                          usersByGroupId[groupId]?.filter(isSelected)?.length === usersByGroupId[groupId]?.length &&
                          usersByGroupId[groupId]?.filter(isSelected)?.length > 0
                        }
                      />
                    }
                  />
                  {groupId !== DEFAULT_GROUP_ID ? (
                    <IconButton onClick={() => deleteGroup(parseInt(groupId, 10))}>
                      <CloseOutlined />
                    </IconButton>
                  ) : null}
                </Stack>
                <Droppable
                  key={groupId}
                  droppableId={`${groupId}`}
                  renderClone={(provided, snapshot, rubric) => {
                    const userId = usersByGroupId[groupId][rubric.source.index];
                    return (
                      <div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
                        <User
                          user={usersById[userId]}
                          isSelected={!!selectedUsers.find((selectedUserId: number) => selectedUserId === userId)}
                        />
                      </div>
                    );
                  }}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        width: "100%",
                        minHeight: "70px",
                        background: snapshot.isDraggingOver ? theme.palette.primary.main : "transparent",
                        opacity: 0.8,
                        borderRadius: 16,
                        transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}>
                      {usersByGroupId[groupId]?.map((userId: number, index: number) => (
                        <Draggable key={userId} draggableId={userId.toString()} index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                paddingTop: 8,
                                paddingLeft: 16,
                                position: "static",
                              }}>
                              <User
                                user={usersById[userId]}
                                isSelected={!!selectedUsers.find((selectedUserId: number) => selectedUserId === userId)}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </Stack>
            ))}
          </DragDropContext>
        </Box>
      </Stack>
    </Stack>
  );
};

export default UserList;
